import React from 'react';
import { Select, Stack } from '@mantine/core';
import IconPickerField from '../../../puck/fields/IconPickerField';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { BlockImageField } from '../../../puck/fields/puckImageField';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import { PxSpacingInput } from '../../../puck/fields/puckPxSpacingField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { useThemeLockedProp } from '../../../puck/fields/themedFieldValues';
import StyleDefaultMark from '../../../puck/fields/StyleDefaultMark';

const ILLUSTRATION_OPTIONS = [
  { value: 'icon', label: 'Icône' },
  { value: 'image', label: 'Photo' },
  { value: 'none', label: 'Aucune' },
];

const SHAPE_OPTIONS = [
  { value: 'round', label: 'Ronde' },
  { value: 'square', label: 'Carrée' },
];

/** Bleu Mantine (blue-6) — couleur effective de l'icône quand `color` est
 *  vide : le picker affiche ce défaut plutôt qu'un faux « transparent ». */
const DEFAULT_ICON_COLOR = '#228be6';

/**
 * Champ composite du bloc InfoCard : type d'illustration (icône / photo /
 * aucune) + réglages correspondants, dans la même section de champ Puck (pas
 * de divider entre eux). Le champ édite la prop `icon` ; les autres props
 * sont écrites via dispatch `replace` (pattern ProposalSectionColumnsField —
 * le onChange d'un champ ne couvre que sa prop).
 */
export default function InfoCardIllustrationField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const isLocked = useThemeLockedProp('infoCard');

  const illustration = itemProps.illustration ?? 'icon';
  const color = itemProps.color ?? '';
  const shape = itemProps.illustrationShape ?? 'round';

  return (
    <Stack gap={12}>
      <StyleDefaultMark propKey="illustration">
        <Select
          size="xs"
          label="Illustration"
          data={ILLUSTRATION_OPTIONS}
          value={illustration}
          onChange={(next) => {
            if (next) updateItemProps({ illustration: next });
          }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      </StyleDefaultMark>
      {illustration === 'icon' && (
        <InspectorRow label="Icône">
          <IconPickerField value={value} onChange={onChange} />
        </InspectorRow>
      )}
      {illustration === 'image' && (
        <BlockImageField
          name="imageUrl"
          value={itemProps.imageUrl ?? ''}
          onChange={(next) => updateItemProps({ imageUrl: next ?? '' })}
          label="Photo"
          focalPointProp="imageUrlFocalPoint"
        />
      )}
      {illustration === 'icon' && !isLocked('color') && (
        <InspectorRow label="Couleur de l'icône">
          <PuckColorFieldWithPalette
            label={null}
            value={color || DEFAULT_ICON_COLOR}
            onChange={(next) => updateItemProps({ color: next ?? '' })}
          />
        </InspectorRow>
      )}
      {illustration !== 'none' && !isLocked('illustrationShape') && (
        <Select
          size="xs"
          label={illustration === 'image' ? 'Forme de la photo' : 'Forme de l\'icône'}
          data={SHAPE_OPTIONS}
          value={shape}
          onChange={(next) => {
            if (next) updateItemProps({ illustrationShape: next });
          }}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      )}
      {illustration !== 'none' && !isLocked('illustrationSize') && (
        <PxSpacingInput
          label={illustration === 'image' ? 'Taille de la photo' : 'Taille de l\'icône'}
          value={itemProps.illustrationSize ?? ''}
          onChange={(next) => updateItemProps({ illustrationSize: next })}
        />
      )}
    </Stack>
  );
}
